<template>
  <div class="dashboard-wrapper">
    <div class="dashboard-header">
      <h2>Welcome back, {{ userDisplayName }}</h2>
      <p class="text-muted">{{ getUserRoleDescription() }}</p>
    </div>

    <!-- Quick Date Navigation -->
    <div class="date-navigation">
      <md-button @click="setToday" :class="{ 'md-primary': isToday }">Today</md-button>
      <md-button @click="setThisWeek" :class="{ 'md-primary': isThisWeek }">This Week</md-button>
      <md-button @click="setThisMonth" :class="{ 'md-primary': isThisMonth }">This Month</md-button>
      <md-button @click="setYesterday" :class="{ 'md-primary': isYesterday }">Yesterday</md-button>
      <md-button @click="setLastWeek" :class="{ 'md-primary': isLastWeek }">Previous Week</md-button>
      <md-button @click="setLastMonth" :class="{ 'md-primary': isLastMonth }">Previous Month</md-button>

      <md-datepicker v-model="selectedDate" @md-changed="onDateChange"/>
    </div>

    <!-- Loading Indicator -->
    <div v-if="isLoading" class="loading-overlay">
      <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
    </div>

    <!-- Dynamic Dashboard Based on User Type -->
    <component
        :is="currentDashboard"
        :date-range="dateRange"
        :key="currentDashboard"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import VendorDashboard from './VendorDashboard.vue';
import DepartmentDashboard from './DepartmentDashboard.vue';
import PortDashboard from './PortDashboard.vue';
import AdminDashboard from './AdminDashboard.vue';
import TodayDashboard from './TodayDashboard.vue';
import DateFormatMixin from "@/mixins/date-format-mixin";

export default {
  name: 'DashboardWrapper',
  mixins: [DateFormatMixin],
  components: {
    VendorDashboard,
    DepartmentDashboard,
    PortDashboard,
    AdminDashboard,
    TodayDashboard
  },
  data: function () {
    return {
      selectedDate: new Date(),
      dateRange: {
        start: null,
        end: null
      }
    };
  },
  computed: {
    ...mapState('profile', ['me']),
    ...mapGetters('dashboard', ['isLoading']),

    userDisplayName: function () {
      if (this.me && this.me.model) {
        var firstName = this.me.model.first_name || '';
        var lastName = this.me.model.last_name || '';
        if (firstName || lastName) {
          return (firstName + ' ' + lastName).trim();
        }
      }
      return 'User';
    },

    userRole: function () {
      if (this.me && this.me.roles && this.me.roles.length) {
        return this.me.roles[0].name;
      }
      return 'Staff';
    },

    userModelType: function () {
      return this.me ? this.me.model_type : null;
    },

    currentDashboard: function () {
      if (this.userModelType === 'Employee') {
        return 'VendorDashboard';
      } else if (this.userRole === 'Administrative Officer-PAM') {
        return 'AdminDashboard';
      } else if (this.userModelType === 'Staff' || this.userModelType === 'Port') {
        return 'DepartmentDashboard';
      } else {
        return 'PortDashboard';
      }
    },

    isToday: function () {
      if (!this.dateRange.start) return false;
      var today = new Date();
      var startDate = new Date(this.dateRange.start);
      return startDate.toDateString() === today.toDateString();
    },

    isYesterday: function () {
      if (!this.dateRange.start) return false;
      var yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      var startDate = new Date(this.dateRange.start);
      return startDate.toDateString() === yesterday.toDateString();
    },

    isThisWeek: function () {
      if (!this.dateRange.start || !this.dateRange.end) return false;
      var today = new Date();
      var dayOfWeek = today.getDay();
      var distanceToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);

      var startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() + distanceToMonday);

      var startDate = new Date(this.dateRange.start);
      return startDate.toDateString() === startOfWeek.toDateString();
    },

    isLastWeek: function () {
      if (!this.dateRange.start || !this.dateRange.end) return false;
      var today = new Date();
      var dayOfWeek = today.getDay();
      var distanceToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);

      var startOfLastWeek = new Date(today);
      startOfLastWeek.setDate(today.getDate() + distanceToMonday - 7);

      var startDate = new Date(this.dateRange.start);
      return startDate.toDateString() === startOfLastWeek.toDateString();
    },

    isThisMonth: function () {
      if (!this.dateRange.start) return false;
      var startDate = new Date(this.dateRange.start);
      var today = new Date();
      return startDate.getMonth() === today.getMonth() &&
          startDate.getFullYear() === today.getFullYear();
    },

    isLastMonth: function () {
      if (!this.dateRange.start) return false;
      var startDate = new Date(this.dateRange.start);
      var today = new Date();
      var lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return startDate.getMonth() === lastMonth.getMonth() &&
          startDate.getFullYear() === lastMonth.getFullYear();
    }
  },
  created: function () {
    this.setThisMonth();
  },
  methods: {
    // Helper fallback if DateFormatMixin doesn't provide formatLocal
    formatLocal: function (d) {
      if (!d) return null;
      var dateObj = new Date(d);
      var year = dateObj.getFullYear();
      var month = String(dateObj.getMonth() + 1).padStart(2, '0');
      var day = String(dateObj.getDate()).padStart(2, '0');
      var hours = String(dateObj.getHours()).padStart(2, '0');
      var minutes = String(dateObj.getMinutes()).padStart(2, '0');
      var seconds = String(dateObj.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    setToday: function () {
      var today = new Date();

      var startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);

      var endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);

      this.selectedDate = today;
      this.dateRange = {
        start: this.formatLocal(startOfDay),
        end: this.formatLocal(endOfDay)
      };
      this.$emit('date-range-change', this.dateRange);
    },

    setYesterday: function () {
      var today = new Date();

      var startOfYesterday = new Date(today);
      startOfYesterday.setDate(today.getDate() - 1);
      startOfYesterday.setHours(0, 0, 0, 0);

      var endOfYesterday = new Date(today);
      endOfYesterday.setDate(today.getDate() - 1);
      endOfYesterday.setHours(23, 59, 59, 999);

      this.dateRange = {
        start: this.formatLocal(startOfYesterday),
        end: this.formatLocal(endOfYesterday)
      };

      this.$emit('date-range-change', this.dateRange);
    },

    setThisWeek: function () {
      var today = new Date();

      var dayOfWeek = today.getDay();
      var distanceToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);

      var startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() + distanceToMonday);
      startOfWeek.setHours(0, 0, 0, 0);

      var endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      this.dateRange = {
        start: this.formatLocal(startOfWeek),
        end: this.formatLocal(endOfWeek)
      };

      this.$emit('date-range-change', this.dateRange);
    },

    setLastWeek: function () {
      var today = new Date();

      var dayOfWeek = today.getDay();
      var distanceToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);

      var startOfLastWeek = new Date(today);
      startOfLastWeek.setDate(today.getDate() + distanceToMonday - 7);
      startOfLastWeek.setHours(0, 0, 0, 0);

      var endOfLastWeek = new Date(startOfLastWeek);
      endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
      endOfLastWeek.setHours(23, 59, 59, 999);

      this.dateRange = {
        start: this.formatLocal(startOfLastWeek),
        end: this.formatLocal(endOfLastWeek)
      };

      this.$emit('date-range-change', this.dateRange);
    },

    setThisMonth: function () {
      var today = new Date();

      var startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
      var endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);

      this.dateRange = {
        start: this.formatLocal(startOfMonth),
        end: this.formatLocal(endOfMonth)
      };

      this.$emit('date-range-change', this.dateRange);
    },

    setLastMonth: function () {
      var today = new Date();

      var startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1, 0, 0, 0, 0);
      var endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);

      this.dateRange = {
        start: this.formatLocal(startOfLastMonth),
        end: this.formatLocal(endOfLastMonth)
      };

      this.$emit('date-range-change', this.dateRange);
    },

    onDateChange: function (date) {
      if (date) {
        var selectedDate = new Date(date);
        var startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);

        var endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);

        this.dateRange = {
          start: this.formatLocal(startOfDay),
          end: this.formatLocal(endOfDay)
        };
        this.$emit('date-range-change', this.dateRange);
      }
    },

    getUserRoleDescription: function () {
      if (this.userModelType === 'Vendor') {
        return 'Vendor Dashboard - Track your labourers';
      }
      if (this.userRole === 'Administrative Officer-PAM') {
        return 'Admin Dashboard - System-wide analytics and monitoring';
      }
      if (this.userModelType === 'Port') {
        return 'Port Dashboard - Monitor operations across departments';
      }
      return 'Department Dashboard - Manage guidances and labourer requests';
    }
  }
};
</script>

<style scoped>
.dashboard-wrapper {
  padding: 20px;
  position: relative;
}

.dashboard-header {
  margin-bottom: 24px;
}

.dashboard-header h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  color: #333;
}

.text-muted {
  color: #666;
  font-size: 14px;
}

.date-navigation {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 8px;
}

.date-navigation .md-button {
  margin: 0;
}

.date-navigation .md-datepicker {
  margin-left: auto;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .dashboard-wrapper {
    padding: 12px;
  }

  .date-navigation {
    flex-direction: column;
    align-items: stretch;
  }

  .date-navigation .md-datepicker {
    margin-left: 0;
  }
}
</style>